import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const videoRef   = useRef<HTMLVideoElement>(null);
  const animRef    = useRef<number>(0);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    if (!canvas || !video) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    // Particles
    const particles = Array.from({ length: 25 }, () => ({
      x:     Math.random(),
      y:     Math.random(),
      r:     Math.random() * 1.2 + 0.2,
      vx:    (Math.random() - 0.5) * 0.0003,
      vy:   -Math.random() * 0.0003 - 0.0001,
      alpha: Math.random() * 0.25 + 0.05,
      color: Math.random() > 0.5 ? '74,158,232' : '201,169,98',
    }));

    const streaks = Array.from({ length: 3 }, (_, i) => ({
      yFrac: 0.30 + i * 0.18,
      phase: Math.random() * Math.PI * 2,
      speed: 0.007 + Math.random() * 0.005,
      alpha: 0.08 + Math.random() * 0.06,
    }));

    let t      = 0;
    let floatT = 0;

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      const W = canvas.width;
      const H = canvas.height;
      ctx.clearRect(0, 0, W, H);

      t      += 0.01;
      floatT += 0.004;
      const floatY = Math.sin(floatT) * 5;

      // Video dimensions — place on right 62% of screen
      const vidRatio = video.videoWidth && video.videoHeight
        ? video.videoWidth / video.videoHeight
        : 16 / 9;

      const iW = W * 0.65;
      const iH = iW / vidRatio;
      const ix = W * 0.35;
      const iy = (H - iH) / 2 + floatY;

      // Soft glow behind video
      const bgGlow = ctx.createRadialGradient(ix + iW*0.45, H*0.5, 20, ix + iW*0.45, H*0.5, iW*0.65);
      bgGlow.addColorStop(0,   'rgba(74,158,232,0.08)');
      bgGlow.addColorStop(0.6, 'rgba(74,158,232,0.02)');
      bgGlow.addColorStop(1,   'rgba(74,158,232,0)');
      ctx.beginPath();
      ctx.arc(ix + iW*0.45, H*0.5, iW*0.65, 0, Math.PI * 2);
      ctx.fillStyle = bgGlow;
      ctx.fill();

      // Draw video frame onto canvas
      if (video.readyState >= 2) {
        ctx.filter = 'brightness(1.3) saturate(1.3) contrast(1.05)';
        ctx.drawImage(video, ix, iy, iW, iH);
        ctx.filter = 'none';
      }

      // Left fade
      const lGrd = ctx.createLinearGradient(ix, 0, ix + iW*0.10, 0);
      lGrd.addColorStop(0, 'rgba(3,8,16,1)');
      lGrd.addColorStop(1, 'rgba(3,8,16,0)');
      ctx.fillStyle = lGrd;
      ctx.fillRect(ix, iy, iW*0.10, iH);

      // Right fade
      const rGrd = ctx.createLinearGradient(ix + iW*0.90, 0, ix + iW, 0);
      rGrd.addColorStop(0, 'rgba(3,8,16,0)');
      rGrd.addColorStop(1, 'rgba(3,8,16,1)');
      ctx.fillStyle = rGrd;
      ctx.fillRect(ix + iW*0.90, iy, iW*0.10, iH);

      // Top fade
      const tGrd = ctx.createLinearGradient(0, iy, 0, iy + iH*0.06);
      tGrd.addColorStop(0, 'rgba(3,8,16,0.7)');
      tGrd.addColorStop(1, 'rgba(3,8,16,0)');
      ctx.fillStyle = tGrd;
      ctx.fillRect(ix, iy, iW, iH*0.06);

      // Bottom fade
      const bGrd = ctx.createLinearGradient(0, iy + iH*0.92, 0, iy + iH);
      bGrd.addColorStop(0, 'rgba(3,8,16,0)');
      bGrd.addColorStop(1, 'rgba(3,8,16,0.7)');
      ctx.fillStyle = bGrd;
      ctx.fillRect(ix, iy + iH*0.92, iW, iH*0.08);

      // LED streaks
      streaks.forEach(s => {
        s.phase += s.speed;
        const pulse = s.alpha * (0.7 + 0.5 * Math.sin(s.phase));
        const sy    = iy + s.yFrac * iH;
        const sGrd  = ctx.createLinearGradient(ix + iW*0.05, sy, ix + iW*0.95, sy);
        sGrd.addColorStop(0,   'rgba(74,158,232,0)');
        sGrd.addColorStop(0.3, `rgba(201,169,98,${pulse})`);
        sGrd.addColorStop(0.6, `rgba(74,158,232,${pulse * 1.3})`);
        sGrd.addColorStop(1,   'rgba(74,158,232,0)');
        ctx.fillStyle = sGrd;
        ctx.fillRect(ix + iW*0.05, sy - 1.5, iW*0.90, 3);
      });

      // Rooftop rays
      for (let b = 0; b < 3; b++) {
        const bx     = ix + iW*0.28 + b * iW*0.16;
        const bAlpha = 0.04 + 0.025 * Math.sin(t * 0.8 + b * 1.1);
        const rayH   = 40 + 12 * Math.sin(t + b);
        const rGrd2  = ctx.createLinearGradient(bx, iy, bx, iy - rayH);
        rGrd2.addColorStop(0, `rgba(201,169,98,${bAlpha})`);
        rGrd2.addColorStop(1, 'rgba(201,169,98,0)');
        ctx.fillStyle = rGrd2;
        ctx.fillRect(bx - 1.5, iy - rayH, 3, rayH);
      }

      // Ground mist
      const mistGrd = ctx.createRadialGradient(ix + iW*0.5, iy + iH, 5, ix + iW*0.5, iy + iH, iW*0.42);
      mistGrd.addColorStop(0,   'rgba(74,158,232,0.08)');
      mistGrd.addColorStop(0.6, 'rgba(74,158,232,0.02)');
      mistGrd.addColorStop(1,   'rgba(74,158,232,0)');
      ctx.save();
      ctx.scale(1, 0.20);
      ctx.beginPath();
      ctx.arc(ix + iW*0.5, (iy + iH) / 0.20, iW*0.38, 0, Math.PI * 2);
      ctx.fillStyle = mistGrd;
      ctx.fill();
      ctx.restore();

      // // Cover bottom-right watermark
      // ctx.fillStyle = 'rgba(3,8,16,0.98)';
      // ctx.fillRect(ix + iW * 0.88, iy + iH * 0.85, iW * 0.14, iH * 0.18);

      // Particles
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.y < 0) { p.y = 0.95; p.x = Math.random(); }
        if (p.x < 0) p.x = 1; if (p.x > 1) p.x = 0;
        const pulse = 0.5 + 0.5 * Math.abs(Math.sin(t * 1.5 + p.x * 10));
        ctx.beginPath();
        ctx.arc(p.x * W, p.y * H, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.alpha * pulse})`;
        ctx.fill();
      });
    };

    // Start drawing once video can play
    video.addEventListener('canplay', draw);
    video.play().catch(() => {});
    draw(); // start immediately even before video loads

    const handleResize = () => resize();
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', handleResize);
      video.removeEventListener('canplay', draw);
    };
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-16" style={{ overflow: 'hidden' }}>

      {/* Hidden video element — source for canvas */}
      <video
        ref={videoRef}
        src="/battery-rack.mp4"
        autoPlay
        loop
        muted
        playsInline
        style={{ display: 'none' }}
      />

      {/* Full viewport canvas */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute', top: 0, left: 0,
          width: '100%', height: '100%',
          zIndex: 0, pointerEvents: 'none',
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/50 via-primary-dark/10 to-transparent" style={{ zIndex: 1 }} />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/20 via-transparent to-transparent" style={{ zIndex: 1 }} />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-gold via-gold/40 to-transparent" style={{ zIndex: 2 }} />

<div className="relative w-full max-w-[1400px] mx-auto py-8 md:py-12" style={{ zIndex: 2, paddingLeft: '2rem', paddingRight: '2rem' }}>        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-4">

          {/* LEFT */}
<div className={`flex-1 max-w-2xl space-y-5 md:space-y-7 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`} style={{ marginLeft: '-4rem' }}>            <div className="flex flex-col items-start">
              <img src="/logo-energy.png" alt="Alebel Energy"
                className="h-28 md:h-40 lg:h-52 w-auto"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>

            <div className="space-y-1">
              <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] text-white leading-[1.1] tracking-tight whitespace-nowrap"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.8), 0 0 40px rgba(3,8,16,0.9)' }}>
                {t('hero.title1')}
              </h1>
              <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.1] tracking-tight text-gold whitespace-nowrap"
                style={{ textShadow: '0 0 40px rgba(74,158,232,0.5), 0 2px 20px rgba(0,0,0,0.9)' }}>
                {t('hero.title2')}
              </h1>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <div className="w-6 h-px bg-gold/60" />
              <span className="text-gold/80 text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.25em] uppercase">
                {t('hero.subtitle')}
              </span>
            </div>

<p className="text-white/85 text-lg sm:text-2xl leading-relaxed max-w-lg font-medium"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>              {t('hero.description')}
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link to="/products"
                className="group inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-gold text-white font-semibold text-sm sm:text-base rounded-lg transition-all duration-300"
                style={{ boxShadow: '0 0 28px rgba(74,158,232,0.3)' }}>
                {t('hero.discoverMore')}
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact"
                className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border-2 border-gold/60 text-gold text-sm sm:text-base font-medium rounded-lg hover:bg-gold/10 hover:border-gold transition-all duration-300">
                {t('hero.getInTouch')}
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 sm:gap-10 pt-4 border-t border-white/10">
              {[
                { value: '98–99.5%', label: t('hero.strategicGrowth'),   className: 'text-3xl sm:text-4xl lg:text-5xl text-gold' },
                { value: '15K+',    label: t('hero.portfolioCompanies'), className: 'text-3xl sm:text-4xl lg:text-4xl text-gold' },
                { value: t('hero.uae'), label: t('hero.headquarters'),   className: 'text-3xl sm:text-4xl lg:text-5xl text-gold' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className={`font-serif font-bold ${stat.className}`} dir="ltr"
                    style={{ textShadow: '0 0 16px rgba(74,158,232,0.4)' }}>
                    {stat.value}
                  </div>
                  <div className="text-white/35 text-xs tracking-wider uppercase mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce" style={{ zIndex: 2 }}>
        <span className="text-white/25 text-xs tracking-widest uppercase">{t('hero.scroll')}</span>
        <i className="ri-arrow-down-line text-gold/50 text-lg" />
      </div>
    </section>
  );
}
